/**
 * Onboarding — First-run agent registration flow
 */

import { AgentIdentity } from './agent-identity.js';

const LOCAL_SKIP_KEY = 'wq_skip_registration_local';

function isLocalTestingHost() {
  const hostname = window.location?.hostname || '';
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function getSkipQueryParam() {
  const search = window.location?.search || '';
  const params = new URLSearchParams(search);
  return params.get('skipRegistration') === '1';
}

function getStoredSkipFlag() {
  try {
    return localStorage.getItem(LOCAL_SKIP_KEY) === '1';
  } catch {
    return false;
  }
}

function setStoredSkipFlag(enabled) {
  try {
    if (enabled) {
      localStorage.setItem(LOCAL_SKIP_KEY, '1');
    } else {
      localStorage.removeItem(LOCAL_SKIP_KEY);
    }
  } catch {
    // Ignore localStorage failures in restricted environments
  }
}

function shouldSkipOnboarding() {
  if (!isLocalTestingHost()) return false;

  if (getSkipQueryParam()) {
    setStoredSkipFlag(true);
    return true;
  }

  return getStoredSkipFlag();
}

/**
 * Check if onboarding is needed and show modal if so
 * @returns {boolean} true if onboarding modal was shown
 */
export function checkOnboarding() {
  if (shouldSkipOnboarding()) return false;
  if (AgentIdentity.isRegistered()) return false;
  showOnboardingModal();
  return true;
}

/**
 * Show the onboarding modal
 */
function showOnboardingModal() {
  const modal = document.getElementById('wq-onboarding-modal');
  if (!modal) return;
  modal.classList.add('wq-modal--open');
  document.addEventListener('keydown', onboardingEscHandler);
  const firstInput = modal.querySelector('input');
  if (firstInput) firstInput.focus();
}

function onboardingEscHandler(e) {
  if (e.key === 'Escape') hideOnboardingModal();
}

/**
 * Hide the onboarding modal
 */
function hideOnboardingModal() {
  const modal = document.getElementById('wq-onboarding-modal');
  if (!modal) return;
  modal.classList.remove('wq-modal--open');
  document.removeEventListener('keydown', onboardingEscHandler);
}

function skipOnboardingForLocalTesting() {
  if (!isLocalTestingHost()) return;
  setStoredSkipFlag(true);
  hideOnboardingModal();
}

/**
 * Handle onboarding form submission
 * @param {Event} e - Form submit event
 */
export async function handleOnboardingSubmit(e) {
  e.preventDefault();
  const form = e.target;
  const submitBtn = form.querySelector('[type="submit"]');

  const data = {
    npn: form.querySelector('#wq-onboard-npn').value.trim(),
    name: form.querySelector('#wq-onboard-name').value.trim(),
    email: form.querySelector('#wq-onboard-email').value.trim(),
    phone: form.querySelector('#wq-onboard-phone').value.trim(),
    title: form.querySelector('#wq-onboard-title').value.trim() || 'Licensed Insurance Agent',
    tagline: form.querySelector('#wq-onboard-tagline').value.trim() || '',
  };

  // Validate NPN (7-10 digits)
  if (!/^\d{7,10}$/.test(data.npn)) {
    showFieldError('wq-onboard-npn', 'NPN must be 7-10 digits');
    return;
  }

  if (!data.name) {
    showFieldError('wq-onboard-name', 'Name is required');
    return;
  }

  if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    showFieldError('wq-onboard-email', 'Valid email is required');
    return;
  }

  submitBtn.disabled = true;
  submitBtn.textContent = 'Registering...';

  try {
    const profile = await AgentIdentity.registerAgent(data);

    // Update existing agentBranding global for backward compatibility
    if (typeof window.agentBranding !== 'undefined') {
      window.agentBranding = AgentIdentity.toBranding(profile);
      if (typeof window.applyAgentBranding === 'function') {
        window.applyAgentBranding();
      }
    }

    hideOnboardingModal();
  } catch (err) {
    const errorEl = form.querySelector('.wq-onboard-error');
    if (errorEl) {
      errorEl.textContent = err.message;
      errorEl.style.display = 'block';
    }
  } finally {
    submitBtn.disabled = false;
    submitBtn.textContent = 'Get Started';
  }
}

function showFieldError(fieldId, message) {
  const field = document.getElementById(fieldId);
  if (!field) return;
  const errorEl = field.parentElement.querySelector('.wq-field-error');
  if (errorEl) {
    errorEl.textContent = message;
    errorEl.style.display = 'block';
  }
  field.focus();
}

/**
 * Initialize onboarding — call on page load
 */
export function init() {
  const form = document.getElementById('wq-onboarding-form');
  if (form) {
    form.addEventListener('submit', handleOnboardingSubmit);
  }

  const skipBtn = document.getElementById('wq-onboard-skip');
  if (skipBtn && isLocalTestingHost()) {
    skipBtn.style.display = 'block';
    skipBtn.addEventListener('click', skipOnboardingForLocalTesting);
  }

  return checkOnboarding();
}

export const Onboarding = {
  checkOnboarding,
  handleOnboardingSubmit,
  init,
  shouldSkipOnboarding,
};
